import React, { useState, useEffect } from 'react';
import { ArrowRight, X, ChevronDown } from 'lucide-react';
import AbReportDetail from './AbReportDetail';
import { useAuth } from '../context/AuthContext';
import { authenticatedFetch } from '../context/apiClient';

const ModelSelector = () => {
  const [selectedModels, setSelectedModels] = useState([null, null]);
  const [showComparison, setShowComparison] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState({ first: false, second: false });
  const [models, setModels] = useState([]);
  const { user, logout } = useAuth();

  useEffect(() => {
    const fetchModels = async () => {
      if (!user?.token) return;
      try {
        const response = await authenticatedFetch('/api/history', {}, user.token, logout);
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        const data = await response.json();
        const formattedModels = Object.values(data).map(item => {
          const totalJailbroken = parseInt(item.total_jailbroken, 10);
          const totalQueries = parseInt(item.total_queries, 10);
          const jailbreakRate = totalQueries > 0 ? Math.round((totalJailbroken / totalQueries) * 100) : 0;
          
          return {
            id: item.scan_id,
            name: item.target_llm,
            version: new Date(item.created_at).toLocaleDateString(),
            jailbreakRate: jailbreakRate,
            totalJailbreaks: totalJailbroken,
          };
        });
        setModels(formattedModels);
      } catch (error) {
        console.error("Failed to fetch models:", error);
      }
    };

    fetchModels();
  }, [user, logout]);

  const handleModelSelect = (position, modelId) => {
    const newSelection = [...selectedModels];
    newSelection[position] = modelId;
    setSelectedModels(newSelection);
    setDropdownOpen({ first: false, second: false });
  };

  const clearSelection = (position) => {
    const newSelection = [...selectedModels];
    newSelection[position] = null;
    setSelectedModels(newSelection);
  };

  const handleCompare = () => {
    if (selectedModels[0] && selectedModels[1]) {
      console.log("Selected models:", selectedModels);
      setShowComparison(true);
    }
  };

  const getModelById = (id) => models.find(m => m.id === id);

  const ModelDropdown = ({ position, label }) => {
    const isOpen = position === 0 ? dropdownOpen.first : dropdownOpen.second;
    const selectedId = selectedModels[position];
    const selectedModel = selectedId ? getModelById(selectedId) : null;
    const otherSelectedId = selectedModels[position === 0 ? 1 : 0];

    return (
      <div className="relative flex-1">
        <label className="block text-sm font-semibold text-slate-700 mb-2">{label}</label>
        <button
          onClick={() => setDropdownOpen({ 
            first: position === 0 ? !isOpen : false, 
            second: position === 1 ? !isOpen : false 
          })}
          className="w-full bg-white border-2 border-slate-300 rounded-lg px-4 py-4 text-left hover:border-blue-400 focus:border-blue-500 focus:outline-none transition-colors flex items-center justify-between"
        >
          {selectedModel ? (
            <div className="flex-1">
              <div className="font-semibold text-slate-800">{selectedModel.name}</div>
              <div className="text-sm text-slate-500">
                {selectedModel.version} • {selectedModel.jailbreakRate}% jailbreak rate
              </div>
            </div>
          ) : (
            <span className="text-slate-400">Select a model...</span>
          )}
          <div className="flex items-center gap-2">
            {selectedModel && (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  clearSelection(position);
                }}
                className="p-1 hover:bg-slate-100 rounded"
              >
                <X className="w-4 h-4 text-slate-500" />
              </button>
            )}
            <ChevronDown className={`w-5 h-5 text-slate-500 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
          </div>
        </button>

        {isOpen && (
          <>
            <div 
              className="fixed inset-0 z-10" 
              onClick={() => setDropdownOpen({ first: false, second: false })}
            ></div>
            <div className="absolute z-20 w-full mt-2 bg-white border border-slate-200 rounded-lg shadow-xl max-h-80 overflow-y-auto">
              {models.map((model) => {
                const isDisabled = model.id === otherSelectedId;
                const isSelected = model.id === selectedId;

                return (
                  <button
                    key={model.id}
                    onClick={() => !isDisabled && handleModelSelect(position, model.id)}
                    disabled={isDisabled}
                    className={`w-full px-4 py-3 text-left hover:bg-blue-50 border-b border-slate-100 last:border-0 transition-colors ${
                      isDisabled ? 'opacity-40 cursor-not-allowed bg-slate-50' : ''
                    } ${isSelected ? 'bg-blue-50 border-l-4 border-l-blue-500' : ''}`}
                  >
                    <div className="flex justify-between items-start">
                      <div className="flex-1">
                        <div className="font-semibold text-slate-800">{model.name}</div>
                        <div className="text-sm text-slate-500">{model.version}</div>
                      </div>
                      <div className="text-right">
                        <div className={`text-lg font-bold ${
                          model.jailbreakRate < 70 ? 'text-green-600' : 
                          model.jailbreakRate < 80 ? 'text-orange-600' : 'text-red-600'
                        }`}>
                          {model.jailbreakRate}%
                        </div>
                        <div className="text-xs text-slate-500">{model.totalJailbreaks} jailbreaks</div>
                      </div>
                    </div>
                  </button>
                );
              })}
            </div>
          </>
        )}
      </div>
    );
  };

  if (showComparison) {
    const model1 = getModelById(selectedModels[0]);
    const model2 = getModelById(selectedModels[1]);
    const vulnChange = ((model2.jailbreakRate - model1.jailbreakRate) / model1.jailbreakRate * 100).toFixed(2);
    const additionalJailbreaks = model2.totalJailbreaks - model1.totalJailbreaks;

    return (
      <div className="min-h-screen">
         <div className="max-w-[1400px] mx-auto">
            <AbReportDetail selectedModels={selectedModels} onBack={() => setShowComparison(false)}/>
          </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen   p-8">
      <div className="max-w-4xl mx-auto">
        <div className="text-center mb-12">
          <h1 className="text-4xl font-bold text-slate-800 mb-4">
            Select Two Assessments to Compare
          </h1>
          <p className="text-slate-600">
            Choose two AI model assessments to compare their vulnerability metrics
          </p>
        </div>

        <div className="bg-white rounded-xl shadow-lg p-8 mb-8">
          <div className="flex gap-6 mb-6">
            <ModelDropdown position={0} label="First Model" />
            <div className="flex items-end pb-4">
              <div className="text-3xl text-slate-300 font-bold">VS</div>
            </div>
            <ModelDropdown position={1} label="Second Model" />
          </div>

          {selectedModels[0] && selectedModels[1] && (
            <div className="border-t pt-6 mt-6">
              <div className="grid grid-cols-2 gap-6 mb-6">
                <div className="bg-blue-50 rounded-lg p-4">
                  <div className="text-sm text-slate-600 mb-1">Model 1</div>
                  <div className="font-bold text-slate-800">{getModelById(selectedModels[0]).name}</div>
                  <div className="text-2xl font-bold text-blue-600 mt-2">
                    {getModelById(selectedModels[0]).jailbreakRate}%
                  </div>
                </div>
                <div className="bg-orange-50 rounded-lg p-4">
                  <div className="text-sm text-slate-600 mb-1">Model 2</div>
                  <div className="font-bold text-slate-800">{getModelById(selectedModels[1]).name}</div>
                  <div className="text-2xl font-bold text-orange-600 mt-2">
                    {getModelById(selectedModels[1]).jailbreakRate}%
                  </div>
                </div>
              </div>
            </div>
          )}

          <button
            onClick={handleCompare}
            disabled={!selectedModels[0] || !selectedModels[1]}
            className={`w-full px-8 py-4 rounded-lg font-semibold text-lg flex items-center justify-center gap-3 transition-all ${
              selectedModels[0] && selectedModels[1]
                ? 'bg-blue-600 text-white hover:bg-blue-700 shadow-lg hover:shadow-xl'
                : 'bg-slate-300 text-slate-500 cursor-not-allowed'
            }`}
          >
            Compare Models
            <ArrowRight className="w-5 h-5" />
          </button>
        </div>
      </div>
    </div>
  );
};

export default ModelSelector;